import { useState, useEffect } from "react";
import { motion, type Variants } from "framer-motion";
import { AnimatedLogo } from "./AnimatedLogo";
import { ArchitectureVisualizer } from "./ArchitectureVisualizer";
import { getSiteContent } from "../utils/adminStore";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export function Hero() {
  const [content, setContent] = useState(getSiteContent());

  useEffect(() => {
    const sync = () => setContent(getSiteContent());
    window.addEventListener("radiece_admin_update", sync);
    return () => window.removeEventListener("radiece_admin_update", sync);
  }, []);

  return (
    <section className="no-print relative bg-gradient-to-br from-[#080d16] via-[#0b131e] to-[#080d16] pt-30 pb-16 sm:pt-36 sm:pb-24 overflow-hidden text-white border-b border-slate-800/80">
      {/* Ambient Radial Lighting Glow */}
      <motion.div
        animate={{
          scale: [1, 1.15, 1],
          opacity: [0.15, 0.25, 0.15],
        }}
        transition={{
          duration: 6,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[32rem] h-[32rem] bg-primary/15 rounded-full blur-3xl pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 50%, #39b7e9 0%, transparent 50%), radial-gradient(circle at 75% 50%, #39b7e9 0%, transparent 50%)",
        }}
      />
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center"
      >
        {/* Full Brand Logo */}
        <motion.div variants={itemVariants}>
          <AnimatedLogo className="w-64 sm:w-80 h-auto mb-6" />
        </motion.div>

        {/* Location & Status Badge */}
        <motion.div
          variants={itemVariants}
          className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6 backdrop-blur-sm transition-all hover:border-primary/40 shadow-xs"
        >
          <span className="w-2 h-2 bg-primary rounded-full animate-pulse" />
          <span className="text-slate-200 text-xs font-medium tracking-wide uppercase">
            Kakkanad, Ernakulam, Kerala • Deep-Tech Practice
          </span>
        </motion.div>

        {/* Value Proposition */}
        <motion.h1
          variants={itemVariants}
          className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-100 max-w-4xl mx-auto leading-tight tracking-tight"
        >
          {content.heroTagline}
        </motion.h1>

        <motion.p
          variants={itemVariants}
          className="mt-4 text-sm sm:text-base text-slate-300 max-w-3xl mx-auto leading-relaxed font-light"
        >
          {content.heroSubtext}
        </motion.p>

        {/* 6 Core Service Verticals */}
        <motion.div
          variants={itemVariants}
          className="mt-8 flex flex-wrap justify-center gap-2 text-xs text-slate-200"
        >
          {[
            "IT & ITeS Software",
            "SaaS & Platforms (RaBuOS)",
            "Hardware Prototyping & R&D",
            "Robotics & Automation",
            "Security & Surveillance",
            "Technical Education",
          ].map((s) => (
            <motion.span
              key={s}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.96 }}
              className="bg-white/5 border border-white/15 rounded-full px-3.5 py-1.5 font-medium hover:border-primary hover:bg-white/10 transition-colors cursor-default"
            >
              {s}
            </motion.span>
          ))}
        </motion.div>

        {/* CTAs */}
        <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            href="/services"
            aria-label="Explore Our Engineering Capabilities"
            className="btn-shine inline-flex items-center gap-2 bg-primary hover:bg-sky-400 text-slate-950 font-bold px-7 py-3 rounded-xl shadow-lg shadow-primary/25 hover:shadow-primary/40 text-xs sm:text-sm uppercase tracking-wider"
          >
            Explore Capabilities →
          </motion.a>
          <motion.a
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.96 }}
            href="/blog"
            aria-label="Read Technical Blog"
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3 rounded-xl border border-white/20 text-xs sm:text-sm uppercase tracking-wider transition-colors"
          >
            Technical Blog & Publications
          </motion.a>
        </motion.div>

        {/* Dual-Mode Architecture Visualizer Embedded in Hero */}
        <motion.div variants={itemVariants} className="w-full">
          <ArchitectureVisualizer />
        </motion.div>
      </motion.div>
    </section>
  );
}
